<template>
  <div class="timesheet-layout d-flex flex-row w-100 h-100 bg-white" style="min-height: 0;">
    <div class="d-flex flex-column flex-grow-1 h-100 overflow-hidden" style="min-width: 0;">
      <div v-if="!readonly && workers.length > 0" class="d-flex align-items-center justify-content-between mb-3 flex-shrink-0 px-2">
        <div class="d-flex align-items-center">
          <select v-model="currentMonth" class="custom-select-filters mr-2">
            <option v-for="month in availableMonths" :key="month.index" :value="month.index">{{ month.name }}</option>
          </select>
          <select v-model="currentYear" class="custom-select-filters mr-2">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
          <div class="d-flex">
            <input
              type="radio"
              id="calc-total"
              class="d-none"
              :checked="calcMode === 'total' && showCalculations"
            >
            <label
              class="mb-0 cursor-pointer"
              :class="[calcMode === 'total' && showCalculations ? 'custom-btn-primary' : 'custom-btn-secondary']"
              style="border-top-right-radius: 0 !important; border-bottom-right-radius: 0 !important;"
              @click="toggleTotalCalc"
            >
              Расчёты
            </label>
            <input
              type="radio"
              class="d-none"
              :checked="calcMode === 'individual'"
            >
            <label
              id="calc-individual"
              class="mb-0 cursor-pointer"
              :class="[calcMode === 'individual' ? 'custom-btn-primary' : 'custom-btn-secondary']"
              style="border-top-left-radius: 0 !important; border-bottom-left-radius: 0 !important; margin-left: -1px !important;"
              @click="toggleIndividualCalc"
            >
              Отдельные расчёты
            </label>
          </div>
        </div>

        <div class="d-flex align-items-center">
          <button
            id="assign-btn"
            type="button"
            class="custom-btn-secondary mr-2"
            :class="{ 'is-btn-flashing': flashState.assignBtn, 'active-mode': activeMode === 'assign' }"
            @click="toggleMode('assign')"
          >
            <v-icon name="buttons/plus" :size="16"/>
          </button>
          <b-tooltip target="assign-btn" triggers="hover" custom-class="custom-tooltip" noninteractive>
            Назначить
          </b-tooltip>
          <button
            id="remove-btn"
            type="button"
            class="custom-btn-secondary mr-2 text-danger"
            :class="{ 'is-btn-flashing': flashState.removeBtn, 'active-mode': activeMode === 'remove' }"
            @click="toggleMode('remove')"
          >
            <v-icon name="buttons/trash" :size="16"/>
          </button>
          <b-tooltip target="remove-btn" triggers="hover" custom-class="custom-tooltip" noninteractive>
            Удалить
          </b-tooltip>
          <button
            id="planning-btn"
            type="button"
            class="custom-btn-secondary mr-2"
            :class="{ 'is-btn-flashing': flashState.planBtn, 'active-mode': activeMode === 'plan' }"
            @click="toggleMode('plan')"
          >
            <v-icon name="buttons/calendar" :size="16"/>
          </button>
          <b-tooltip target="planning-btn" triggers="hover" custom-class="custom-tooltip" noninteractive>
            Запланировать
          </b-tooltip>
        </div>
      </div>

      <div class="flex-grow-1 position-relative mx-2 mb-2" style="min-height: 0;">
        <div
          ref="scrollContainer"
          class="timesheet-container position-absolute w-100 h-100 hardware-accel"
          :class="{
            'is-planning': activeMode === 'plan',
            'scroll-locked': scrollLocked,
            'flash-targets-assign': flashState.table && activeMode === 'assign',
            'flash-targets-remove': flashState.table && activeMode === 'remove',
            'flash-targets-plan': flashState.table && activeMode === 'plan'
          }"
          @contextmenu.prevent="clearHighlight"
        >
          <div v-if="workers.length > 0" class="timesheet-grid d-flex w-100 h-100">
            <div class="timesheet-fixed-panel">
              <div class="timesheet-header-fixed">
                <div class="timesheet-row mb-1">
                  <div class="worker-col-spacer"></div>
                </div>
                <div class="timesheet-row">
                  <div class="worker-col-header text-muted">Исполнители</div>
                  <div id="price-target" style="cursor: pointer;" class="price-col-header text-muted">Ставка</div>
                </div>
              </div>
              <div ref="fixedBody" class="timesheet-body-fixed">
                <div
                  v-for="worker in workers"
                  :key="worker.id"
                  class="timesheet-row"
                >
                  <div class="worker-col-name text-truncate" :title="worker.name">
                    {{ worker.name }}
                  </div>
                  <div class="price-col-cell">
                    <input
                      v-model.number="workerPrices[worker.id]"
                      type="number"
                      class="price-cell-input"
                      :min="0"
                      placeholder="0"
                      :readonly="readonly"
                    >
                  </div>
                </div>
              </div>
            </div>

            <div ref="scrollablePanel" class="timesheet-scrollable-panel flex-grow-1" @scroll="syncScroll">
              <div class="timesheet-header-scrollable">
                <div class="timesheet-row mb-1">
                  <div
                    v-for="day in daysInCurrentMonth"
                    :key="'count-' + day.dateString"
                    class="day-counter-cell"
                  >
                    <span v-if="columnWorkerCounts[day.dateString] > 0">
                      {{ columnWorkerCounts[day.dateString] }} чел.
                    </span>
                  </div>
                </div>
                <div class="timesheet-row">
                  <div
                    v-for="(day, dIndex) in daysInCurrentMonth"
                    :key="day.dateString"
                    :id="day.dayNumber === 5 ? 'target-cell-5' : null"
                    class="day-header-cell"
                    :class="{
                      'is-weekend': day.isWeekend,
                      'is-today': day.isToday,
                      'is-col-hovered': hoveredDayIndex === dIndex,
                      'is-clickable': !readonly && !planningMode,
                      'flash-calc-target': calcMode === 'individual' && groupedPlannedWorkers[day.dateString]
                    }"
                    @click="handleHeaderClick(day.dateString)"
                  >
                    <span class="day-name">{{ day.dayName }}</span>
                    <span class="day-number">{{ day.dayNumber }}</span>
                  </div>
                </div>
              </div>

              <div class="timesheet-body-scrollable" @mouseleave="hoveredDayIndex = null">
                <div
                  v-for="(worker, wIndex) in workers"
                  :key="worker.id"
                  class="timesheet-row"
                >
                  <div
                    v-for="(day, dIndex) in daysInCurrentMonth"
                    :key="day.dateString"
                    :id="day.dayNumber === 1 ? 'target-cell-1' : `cell-${worker.id}_${day.dateString}`"
                    class="day-cell"
                    :class="{
                      'is-selected': selectedCells.has(worker.id + '_' + day.dateString),
                      'is-highlighted': highlightedCells[worker.id + '_' + day.dateString],
                      'is-readonly': readonly,
                      'is-disabled': activeMode === 'plan' && !selectedCells.has(worker.id + '_' + day.dateString),
                      'is-col-hovered': hoveredDayIndex === dIndex,
                      'is-dragged': activeSelectionBounds && wIndex >= activeSelectionBounds.minR && wIndex <= activeSelectionBounds.maxR && dIndex >= activeSelectionBounds.minC && dIndex <= activeSelectionBounds.maxC
                    }"
                    @mouseenter="onMouseEnter(wIndex, dIndex)"
                    @mousedown.prevent="onMouseDown(wIndex, dIndex)"
                  >
                    <v-icon v-if="taskWorkerCells[worker.id + '_' + day.dateString]" name="buttons/check" :size="11" class="text-white"/>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="!readonly" class="empty-state-container mt-5">
            <v-icon name="navbar/empty_table" :size="90"/>
            <h5>Нет исполнителей для отображения.</h5>
            <p>Сначала добавьте исполнителей, прежде чем перейти к табелю</p>
          </div>
          <div v-else class="p-3 text-muted text-center w-100">
            <v-icon name="navbar/empty_table" :size="90"/>
            Нет исполнителей для отображения
          </div>
        </div>
      </div>

      <transition name="layout-slide-up">
        <div v-show="showCalculations" class="calculations-panel flex-shrink-0 px-3">
          <div class="text-muted text-small border-bottom font-weight-bold mb-1 pb-1">
            <span v-if="calcMode === 'total'">Сводные расчёты по всем заявкам ({{Object.keys(groupedPlannedWorkers).length }})</span>
            <span v-else>Расчёты за: {{ activeCalcDate }}</span>
          </div>
          <div class="row text-small pb-4">
            <div class="col-4 d-flex flex-column gap-2 border-right">
              <div class="d-flex justify-content-between">
                <span class="text-muted">Стоимость за ед.:</span>
                <strong>{{ priceFormat(summaryTotals.payment_first) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-muted">
                  Кол-во
                  <template v-if="locationWorks.length">
                    ({{ getUnitByCode(locationWorks[0].unit_code).shortName }}):
                  </template>
                </span>
                <strong>{{ priceFormat(summaryTotals.total_hours) }}</strong>
              </div>
            </div>

            <div class="col-4 d-flex flex-column gap-2 border-right">
              <div class="d-flex justify-content-between">
                <span class="text-muted">Исполнителям:</span>
                <strong>{{ priceFormat(summaryTotals.total_worker_price) }}</strong>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted">
                  С учетом коммиссии СМЗ
                  <small>({{ summaryTotals.smz_cost }}%)</small>
                </span>
                <strong>{{ priceFormat(summaryTotals.total_worker_price_with_comiss) }}</strong>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted">Сумма с заказчика:</span>
                <strong>{{ priceFormat(summaryTotals.total_price) }}</strong>
              </div>
            </div>

            <div class="col-4 d-flex flex-column gap-2">
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted">
                  Маржинальность
                  <small>(без учета расходов)</small>
                </span>
                <strong class="text-nowrap" :class="summaryTotals.marginality > 0 ? 'text-success' : 'text-danger'">
                  {{ summaryTotals.marginality }} %
                </strong>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-muted">Дельта:</span>
                <strong>{{ priceFormat(summaryTotals.total_delta) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-muted">Менеджеру:</span>
                <strong>{{ priceFormat(summaryTotals.total_manager) }}</strong>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted">
                  Руководителю
                  <small>(без учета расходов)</small>
                </span>
                <strong class="text-nowrap">{{ priceFormat(summaryTotals.total_project) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <transition name="layout-slide-right">
      <div v-if="isPlaningSidebarOpen" class="planning-sidebar h-100 flex-shrink-0 d-flex flex-column px-2 pt-2">
        <b-card class="custom-card">
          <b-tabs id="planning-tabs" class="custom-tabs normal-tabs flex-grow-1 d-flex flex-column" style="min-height: 0;">
            <b-tab title="Работы" active>
              <b-card class="custom-card my-2">
                <div class="w-100 d-flex flex-column">
                  <b-form-group label="Время начала" class="w-100 mb-2">
                    <b-form-input v-model="workerItems.start_time" type="time"/>
                  </b-form-group>
                  <b-form-group v-if="activePlanCells.length <= 1" label="Дата окончания" class="w-100 mb-2">
                    <b-form-input v-model="workerItems.end_date" type="date"/>
                  </b-form-group>
                  <b-form-group label="Время окончания" class="w-100">
                    <b-form-input v-model="workerItems.end_time" type="time"/>
                  </b-form-group>
                </div>
              </b-card>
              <b-card class="custom-card mt-2">
                <div v-for="(work, index) in locationWorks" :key="index">
                  <b-form-group label="Вид работ" class="mb-2">
                    <div class="position-relative">
                      <b-form-input :value="work.work_type_name" readonly />
                      <div class="meta-info-overlay">
                        {{ work.rate }} {{ getUnitByCode(work.unit_code).shortName }}
                      </div>
                    </div>
                  </b-form-group>
                  <b-form-group label="Кол-во">
                    <b-form-input v-if="work.unit_code === '539'" :value="calculatedHours" disabled />
                    <b-form-input v-else v-model.number="workerItems.total_units" type="number" />
                  </b-form-group>
                </div>

                <div v-if="locationWorks.length === 0" class="text-center text-muted p-2">
                  Вид работ не выбран
                </div>
              </b-card>
            </b-tab>
            <b-tab title="Расходы">
              <b-card class="custom-card my-2">
                <div class="w-100 d-flex flex-column">
                  <b-form-group label="Обед, ч." class="mb-2">
                    <b-form-input v-model.number="workerItems.dinner" type="number" step="0.5" min="0"/>
                  </b-form-group>
                  <b-form-group label="Трансфер, ₽" class="mb-2">
                    <b-form-input v-model.number="workerItems.road_price" type="number" min="0"/>
                  </b-form-group>
                  <b-form-group label="Аванс, ₽">
                    <b-form-input v-model.number="workerItems.prepaid" type="number" min="0"/>
                  </b-form-group>
                </div>
              </b-card>
            </b-tab>
            <b-tab title="Услуги">
              <div
                v-for="(service, index) in workerItems.task_worker_services"
                :key="index"
                class="custom-card my-2 p-2"
              >
                <b-form-group class="mb-2">
                  <template #label>
                    <div class="d-flex align-items-center justify-content-between">
                      <span>Вид услуги</span>
                      <b-button variant="link" class="p-0 text-danger" @click="removeService(index)">
                        <v-icon name="buttons/trash" :size="14"/>
                      </b-button>
                    </div>
                  </template>
                  <b-form-select
                    v-model="service.location_work_service_id"
                    :options="services"
                    size="sm"
                    @change="onServiceSelected($event, index)"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>Выберите услугу</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>

                <div class="row no-gutters">
                  <div class="col-6 pr-1">
                    <b-form-group label="Кол-во" class="mb-0">
                      <b-form-input
                        :value="service.quantity"
                        type="number"
                        size="sm"
                        @input="onServiceQuantityInput(index, $event)"
                      />
                    </b-form-group>
                  </div>
                  <div class="col-6 pl-1 d-flex flex-column justify-content-end">
                    <div v-if="service.location_work_service_id" class="text-right pb-1">
                      <div style="font-size: 0.75rem; line-height: 1;">
                        <strong>Итого: {{ service.total_price }} ₽</strong>
                      </div>
                      <div class="text-muted mt-2" style="font-size: 0.75rem; line-height: 1;">
                        Цена: {{ service.price }} ₽ / {{ getUnitByCode(service.unit_code).shortName }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <b-button
                class="mt-2 custom-btn-dashed"
                block
                @click="addService"
              >
                <v-icon name="buttons/plus" :size="11"/>
                Добавить услугу
              </b-button>
            </b-tab>
          </b-tabs>

          <div class="d-flex justify-content-center flex-shrink-0">
            <b-button class="custom-btn-secondary mr-2" @click="cancelPlanning">Закрыть</b-button>
            <b-button class="custom-btn-primary" @click="savePlaningData">Применить</b-button>
          </div>
        </b-card>
      </div>
    </transition>

    <b-popover
      v-if="activePopover.targetId"
      :target="activePopover.targetId"
      :show.sync="activePopover.show"
      @shown="addOutsideClickListener"
      @hidden="removeOutsideClickListener"
      boundary="window"
      placement="bottom"
      triggers="manual"
      container="body"
      custom-class="timesheet-popover"
    >
      <button
        type="button"
        class="close"
        style="position: absolute; top: -3px; right: 0; font-size: 1.2rem; line-height: 1; outline: none;"
        @click="activePopover.show = false"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <div v-if="popoverDetails" class="text-small mx-1">
        <template v-if="popoverDetails.isPlanned">
          <div class="mb-1">Дата и время начала: {{ popoverDetails.startStr }};</div>
          <div class="mb-1">Дата и время окончания: {{ popoverDetails.endStr }};</div>
          <div class="mb-1">Количество работы: {{ popoverDetails.total_units }};</div>
          <div class="mb-1">Ставка: {{ popoverDetails.price }}; Аванс: {{ popoverDetails.prepaid }};</div>
          <div class="mb-1">Обед: {{ popoverDetails.dinner }} час; Трансфер: {{ popoverDetails.road_price }};</div>
          <div class="mb-1 font-weight-bold">Итого: {{ popoverDetails.total_price }};</div>
          <div v-if="popoverDetails.servicesStr" class="mt-2 text-muted">
            Услуги исполнителя: {{ popoverDetails.servicesStr }}
          </div>
        </template>

        <template v-else>
          <div class="text-muted mb-2">Исполнитель ещё не запланирован.</div>
        </template>

        <div class="d-flex justify-content-between gap-2 mt-3">
          <b-button class="custom-btn-primary" @click="editFromPopover">
            Редактировать
          </b-button>
          <b-button class="custom-btn-danger" @click="removeFromPopover">
            Удалить
          </b-button>
        </div>
      </div>
    </b-popover>

    <b-popover
      target="price-target"
      triggers="click"
      placement="right"
      ref="price_popover"
      custom-class="custom-popover"
    >
      <template #title>Назначить всем исполнителям</template>
      <div class="p-1">
        <b-form-group label="Ставка">
          <b-input
            v-model.number="allWorkersPrice"
            type="number"
            :min="0"
            placeholder="Введите сумму"
          />
        </b-form-group>
        <div class="d-flex justify-content-end mt-2">
          <button class="custom-btn-primary" @click="applyWorkersPrice">
            Применить
          </button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import dateFormatter from '../../utilites/dateFormatter'
import unitStore from "../../utilites/unitStore";
import taskService from "./taskService";
import helpers from "../../utilites/helpers";

const WEEK_DAYS =['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTH_NAMES =  ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

const DEFAULT_WORKER = (date = '') => ({
  start_time: '08:00',
  end_date: date,
  end_time: '20:00',
  price: 0.0,
  dinner: 0.0,
  road_price: 0.0,
  prepaid: 0.0,
  total_units: 12,
  task_worker_services: []
});

export default {
  name: 'TaskTimesheet',
  mixins: [ dateFormatter, unitStore, taskService, helpers ],
  props: {
    baseTask: { type: Object, required: true, default: () => ({ task_workers: [], task_location_works: [] }) },
    services: { type: Array, default: () => [] },
    initialSelected: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    scrollLocked: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedCells: new Set(this.initialSelected),
      planningMode: false,
      activePlanCells: [],
      hoveredDayIndex: null,
      selectedArea: null,
      drag: {
        active: false,
        startR: -1,
        startC: -1,
        curR: -1,
        curC: -1,
      },
      autoScroll: {
        active: false,
        mouseY: 0,
        frameId: null
      },
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      taskWorkerCells: {},
      workerItems: {},
      workerPrices: {},
      highlightedCells: {},
      showCalculations: true,
      calcMode: 'total',
      activeCalcDate: null,
      isPlaningSidebarOpen: false,
      activePopover: {
        show: false,
        targetId: null,
        workerId: null,
        date: null
      },
      activeMode: null,
      flashState: {
        assignBtn: false,
        removeBtn: false,
        planBtn: false,
        table: false
      },
      allWorkersPrice: 0,
      isSyncingScroll: false
    }
  },
  mounted() {
    window.addEventListener('mouseup', this.onMouseUp);
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.trackMouse);
    this.removeOutsideClickListener();

    if (this.autoScroll.frameId) {
      cancelAnimationFrame(this.autoScroll.frameId);
    }
  },
  computed: {
    groupedPlannedWorkers() {
      const groups = {};
      const prices = this.workerPrices;

      Object.keys(this.taskWorkerCells).forEach(key => {
        const parts = key.split('_');
        const workerId = parseInt(parts[0], 10);
        const dateStr = parts[1];

        const tw = Object.assign({}, this.taskWorkerCells[key]);

        tw.price = Number(tw.price) || Number(prices[workerId]) || 0;
        tw.total_units = Number(tw.total_units) || 0;
        tw.prepaid = Number(tw.prepaid) || 0;
        tw.road_price = Number(tw.road_price) || 0;
        tw.dinner = Number(tw.dinner) || 0;

        if (tw.total_units === 0 && tw.start_time && tw.end_time) {
          const diffMinutes = this.calcMinutesDiff(tw.start_time, tw.end_time);
          tw.total_units = Math.max(0, (diffMinutes / 60) - tw.dinner);
        }

        tw.total_price = this.calculateTotalWorkerPrice(tw);

        if (!groups[dateStr]) groups[dateStr] = [];
        groups[dateStr].push(tw);
      });

      return groups;
    },
    summaryTotals() {
      if (this.calcMode === 'individual' && this.activeCalcDate) {
        const workers = this.groupedPlannedWorkers[this.activeCalcDate] || [];
        const totals = this.calculateTaskTotals(workers);

        totals.marginality = totals.total_price > 0
          ? ((totals.total_delta / totals.total_price) * 100).toFixed(2)
          : 0;

        return totals;
      }

      const dates = Object.keys(this.groupedPlannedWorkers);

      const grandTotals = {
        payment_first: Number(this.baseTask.payment_first) || 0,
        smz_cost: Number(this.baseTask.smz_cost) || 0,
        total_hours: 0,
        total_worker_price: 0,
        total_worker_price_with_comiss: 0,
        total_price: 0,
        total_delta: 0,
        total_manager: 0,
        total_project: 0,
        marginality: 0
      };

      if (dates.length === 0) return grandTotals;

      dates.forEach(date => {
        const dailyTotals = this.calculateTaskTotals(this.groupedPlannedWorkers[date]);
        grandTotals.total_hours += dailyTotals.total_hours;
        grandTotals.total_worker_price += dailyTotals.total_worker_price;
        grandTotals.total_worker_price_with_comiss += dailyTotals.total_worker_price_with_comiss;
        grandTotals.total_price += dailyTotals.total_price;
        grandTotals.total_delta += dailyTotals.total_delta;
        grandTotals.total_manager += dailyTotals.total_manager;
        grandTotals.total_project += dailyTotals.total_project;
      });

      if (grandTotals.total_price > 0) {
        grandTotals.marginality = ((grandTotals.total_delta / grandTotals.total_price) * 100).toFixed(2);
      }

      return grandTotals;
    },
    workers() {
      return (this.baseTask.task_workers || []).map(w => ({
        ...w,
        id: w.worker_id,
        name: w.name
      }));
    },
    locationWorks() {
      return this.baseTask.task_location_works || [];
    },
    columnWorkerCounts() {
      const counts = {};
      this.daysInCurrentMonth.forEach(day => {
        let count = 0;
        const dateStr = day.dateString;
        this.workers.forEach(worker => {
          if (this.selectedCells.has(`${worker.id}_${dateStr}`)) count++;
        });
        counts[dateStr] = count;
      });
      return counts;
    },
    daysInCurrentMonth() {
      const days = [];
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      const todayString = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

      for (let i = 1; i <= lastDay; i++) {
        const dateObj = new Date(this.currentYear, this.currentMonth, i);
        const dayOfWeek = (dateObj.getDay() + 6) % 7;
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        const dateString = `${y}-${m}-${d}`;

        days.push({
          dateObj,
          dateString,
          dayNumber: i,
          dayName: WEEK_DAYS[dayOfWeek],
          isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
          isToday: dateString === todayString
        });
      }
      return days;
    },
    availableYears() {
      const current = new Date().getFullYear();
      return [current, current + 1];
    },
    availableMonths() {
      const year = new Date().getFullYear();
      const month = new Date().getMonth();
      const months = [];

      for (let i = 0; i < 12; i++) {
        if (this.currentYear > year || i >= month - 1) {
          months.push({ index: i, name: MONTH_NAMES[i] });
        }
      }
      return months;
    },
    calculatedHours() {
      const { start_time, end_time, dinner } = this.workerItems;
      const diffMinutes = this.calcMinutesDiff(start_time, end_time);

      if (diffMinutes === 0 && (!start_time || !end_time)) return 0;

      const total = (diffMinutes / 60) - (parseFloat(dinner) || 0);
      return total > 0 ? total.toFixed(2) : 0;
    },
    activeSelectionBounds() {
      if (!this.drag.active && !this.selectedArea) return null;

      if (this.drag.active) {
        return {
          minR: Math.min(this.drag.startR, this.drag.curR),
          maxR: Math.max(this.drag.startR, this.drag.curR),
          minC: Math.min(this.drag.startC, this.drag.curC),
          maxC: Math.max(this.drag.startC, this.drag.curC)
        };
      }
      return this.selectedArea;
    },
    popoverDetails() {
      const { workerId, date } = this.activePopover;
      if (!workerId || !date) return null;

      const key = this.getCellKey(workerId, date);
      const isPlanned = !!this.taskWorkerCells[key];
      const rawData = this.taskWorkerCells[key] || DEFAULT_WORKER(date);
      const formatDate = (d) => d ? d.split('-').reverse().join('.') : '';

      const price = isPlanned ? (Number(rawData.price) || 0) : (Number(this.workerPrices[workerId]) || 0);
      const totalUnits = Number(rawData.total_units) || 0;
      const totalPrice = totalUnits * price;

      let servicesStr = 'Нет';
      if (rawData.task_worker_services && rawData.task_worker_services.length) {
        servicesStr = rawData.task_worker_services.map(s =>
          `${s.location_work_service_name}, ${s.price}р, ${s.quantity} чел.ч, итого: ${s.total_price}р`
        ).join('; ');
      }

      return {
        isPlanned,
        startStr: `${formatDate(date)}, ${rawData.start_time}`,
        endStr: `${formatDate(rawData.end_date || date)}, ${rawData.end_time}`,
        total_units: totalUnits,
        price,
        prepaid: Number(rawData.prepaid) || 0,
        dinner: Number(rawData.dinner) || 0,
        road_price: Number(rawData.road_price) || 0,
        total_price: totalPrice,
        servicesStr
      };
    },
  },
  watch: {
    workerPrices: {
      handler(newPrices) {
        Object.entries(this.taskWorkerCells).forEach(([key, cellData]) => {
          const workerId = parseInt(key.split('_')[0]);
          const newPrice = Number(newPrices[workerId]) || 0;

          if (Number(cellData.price) !== newPrice) {
            this.$set(this.taskWorkerCells[key], 'price', newPrice);
          }
        });
      }, deep: true
    },
    currentYear() {
      if (!this.availableMonths.some(m => m.index === this.currentMonth)) {
        this.currentMonth = new Date().getMonth();
      }
    },
    initialSelected(newVal) {
      this.selectedCells = new Set(newVal || []);
    },
    workers(newVal) {
      const currentIds = newVal.map(w => w.id);
      Object.keys(this.taskWorkerCells).forEach(key => {
        const workerId = parseInt(key.split('_')[0]);
        if (!currentIds.includes(workerId)) {
          this.selectedCells.delete(key);
          this.$delete(this.taskWorkerCells, key);
        }
      });
      newVal.forEach(w => {
        if (this.workerPrices[w.id] === undefined) {
          this.$set(this.workerPrices, w.id, 0);
        }
      });
    },
    calculatedHours(newVal) {
      const isHours = this.locationWorks.some(w => w.unit_code === '539');
      if (isHours) {
        this.$set(this.workerItems, 'total_units', newVal);
      }
    }
  },
  methods: {
    // Timesheet
    toggleMode(mode) {
      const keys = Object.keys(this.highlightedCells);

      if (this.activeMode === mode) {
        this.cancelPlanning();
        return;
      }

      if (this.activeMode) {
        this.isPlaningSidebarOpen = false;
        this.activePlanCells = [];
        this.activeMode = mode;

        if (this.activeMode === 'plan') this.highlightedCells = {};
        return;
      }

      if (keys.length > 0) {
        this.applyActionToKeys(mode, keys);

        if (mode === 'plan') this.activeMode = 'plan';
        return;
      }

      if (mode === 'remove') this.resetCalcToTotal();


      this.activeMode = mode;
      this.flashState.table = true;
      setTimeout(() => { this.flashState.table = false; }, 600);
      this.$bvToast.toast('Выберите область в табеле для действия', { variant: 'info', autoHideDelay: 2000 });
    },
    applyActionToKeys(mode, keys) {
      if (!keys || keys.length === 0) return;

      if (mode === 'assign') {
        keys.forEach(key => this.selectedCells.add(key));
        this.highlightedCells = {};
      }
      else if (mode === 'remove') {
        this.resetCalcToTotal();

        keys.forEach(key => {
          this.selectedCells.delete(key);

          if (this.taskWorkerCells[key]) this.$delete(this.taskWorkerCells, key);
        });

        this.highlightedCells = {};

        if (this.isPlaningSidebarOpen) this.cancelPlanning();
      }
      else if (mode === 'plan') {
        const targets = keys
          .filter(key => this.selectedCells.has(key))
          .map(k => {
            const [workerId, date] = k.split('_');
            return { workerId: parseInt(workerId), date };
          });

        if (targets.length === 0) {
          this.$bvToast.toast('Среди выделенных нет назначенных исполнителей', { variant: 'warning' });
          return;
        }

        this.activePlanCells = targets;
        this.initSidebar();
      }

      this.selectedCells = new Set(this.selectedCells);
      this.$emit('change', Array.from(this.selectedCells));
      this.$emit('workers-planned', Object.keys(this.taskWorkerCells).length);
    },
    onMouseDown(wIndex, dIndex) {
      if (this.readonly) return;

      this.activePopover.show = false;
      this.drag.active = true;
      this.drag.startR = this.drag.curR = wIndex;
      this.drag.startC = this.drag.curC = dIndex;
      window.addEventListener('mousemove', this.trackMouse);
      this.autoScroll.active = true;
      this.doAutoScroll();
    },
    onMouseEnter(wIndex, dIndex) {
      this.hoveredDayIndex = dIndex;

      if (this.drag.active) {
        this.drag.curR = wIndex;
        this.drag.curC = dIndex;
      }
    },
    onMouseUp(e) {
      if (!this.drag.active) return;

      window.removeEventListener('mousemove', this.trackMouse);
      this.autoScroll.active = false;

      if (this.autoScroll.frameId) cancelAnimationFrame(this.autoScroll.frameId);

      const bounds = this.activeSelectionBounds;
      let processedKeys = [];

      if (!bounds) {
        this.drag.active = false;
        return;
      }

      for (let r = bounds.minR; r <= bounds.maxR; r++) {
        for (let c = bounds.minC; c <= bounds.maxC; c++) {
          const w = this.workers[r];
          const d = this.daysInCurrentMonth[c];
          if (w && d) {
            processedKeys.push(this.getCellKey(w.id, d.dateString));
          }
        }
      }

      const isSingleCell = processedKeys.length === 1;

      if (this.activeMode) {
        if (this.activeMode === 'plan') {
          processedKeys.forEach(key => {
            if (this.selectedCells.has(key)) {
              this.$set(this.highlightedCells, key, true);
            }
          });
        }

        this.applyActionToKeys(this.activeMode, processedKeys);

        if (this.activeMode !== 'plan') {
          this.highlightedCells = {};
        }
      } else {
        if (isSingleCell) {
          const key = processedKeys[0];
          const [wId, dStr] = key.split('_');

          if (this.selectedCells.has(key)) {
            const cellNode = e.target.closest('.day-cell');
            this.openPopover(wId, dStr, cellNode.id);
          } else {
            if (this.highlightedCells[key]) {
              if (this.highlightedCells[key]) this.$delete(this.highlightedCells, key);
            } else {
              this.$set(this.highlightedCells, key, true);
            }
          }
        } else {
          processedKeys.forEach(key => this.$set(this.highlightedCells, key, true));
        }
      }

      this.drag.active = false;
      this.checkFlashingButtons();
    },
    doAutoScroll() {
      if (!this.autoScroll.active) return;

      const container = this.$refs.scrollContainer;
      if (container) {
        const rect = container.getBoundingClientRect();
        const edgeSize = 60;
        const speed = 12;

        if (this.autoScroll.mouseY < rect.top + edgeSize) {
          container.scrollTop -= speed;
        }
        else if (this.autoScroll.mouseY > rect.bottom - edgeSize) {
          container.scrollTop += speed;
        }
      }

      this.autoScroll.frameId = requestAnimationFrame(this.doAutoScroll);
    },
    trackMouse(e) {
      this.autoScroll.mouseY = e.clientY;
    },
    toggleColumn(dateString) {
      if (this.readonly || this.planningMode) return;

      const allSelected = this.workers.every(w =>
        this.selectedCells.has(this.getCellKey(w.id, dateString))
      );

      this.workers.forEach(w => {
        const key = this.getCellKey(w.id, dateString);
        if (allSelected) {
          this.selectedCells.delete(key);
          this.$delete(this.taskWorkerCells, key);
        } else {
          this.selectedCells.add(key);
        }
      });

      this.selectedCells = new Set(this.selectedCells);
      this.$emit('change', Array.from(this.selectedCells));
      this.$emit('workers-planned', Object.keys(this.taskWorkerCells).length);
    },
    getCellKey(workerId, dateString) {
      return `${workerId}_${dateString}`;
    },
    clearHighlight() {
      this.highlightedCells = {};
      this.activeMode = null;
      this.resetCalcToTotal();
      this.activePopover.show = false;
    },
    syncScroll(e) {
      if (this.$refs.fixedBody) {
        window.requestAnimationFrame(() => {
          this.$refs.fixedBody.scrollTop = e.target.scrollTop;
        });
      }
    },
    // Planing
    addOneDay(dateString) {
      const date = new Date(dateString);
      date.setDate(date.getDate() + 1);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    initSidebar() {
      if (this.activePlanCells.length === 0) return;

      const { workerId, date } = this.activePlanCells[0];
      const firstKey = this.getCellKey(workerId, date);
      const existingData = this.taskWorkerCells[firstKey];

      this.isPlaningSidebarOpen = true;

      if (this.activePlanCells.length === 1) {
        this.workerItems = existingData ? JSON.parse(JSON.stringify(existingData)) : DEFAULT_WORKER(date);
        return;
      }

      const allSame = this.activePlanCells.every(cell => {
        const data = this.taskWorkerCells[this.getCellKey(cell.workerId, cell.date)];
        return JSON.stringify(data) === JSON.stringify(existingData);
      });

      if (allSame && existingData) {
        this.workerItems = JSON.parse(JSON.stringify(existingData));
      } else {
        this.workerItems = DEFAULT_WORKER('');
        if (!allSame) {
          this.$bvToast.toast('У этих заявок отличаются данные планирования. Показаны стандартные значение, сохранение перезапишет данные для всех выбранных исполнителей.', {
            title: 'Внимание',
            variant: 'warning',
            autoHideDelay: 5000
          });
        }
      }
    },
    savePlaningData() {
      if (this.activePlanCells.length === 1) {
        if (new Date(this.workerItems.end_date) < new Date(this.activePlanCells[0].date)) {
          return this.$bvToast.toast('Дата окончания не может быть раньше начала!', { variant: 'danger' });
        }
      }

      this.activePlanCells.forEach(cell => {
        const key = this.getCellKey(cell.workerId, cell.date);

        let finalData = Object.assign({}, this.workerItems, {
          task_worker_services: this.workerItems.task_worker_services.map(s => Object.assign({}, s))
        });

        finalData.price = Number(finalData.price) || Number(this.workerPrices[cell.workerId]) || 0;

        if (finalData.end_time <= finalData.start_time || this.activePlanCells.length > 1) {
          finalData.end_date = (finalData.end_time <= finalData.start_time) ? this.addOneDay(cell.date) : cell.date;
        }

        this.$set(this.taskWorkerCells, key, finalData);
      });

      this.highlightedCells = {};
      this.$emit('workers-planned', Object.keys(this.taskWorkerCells).length);
    },
    cancelPlanning() {
      this.highlightedCells = {};
      this.isPlaningSidebarOpen = false;
      this.activeMode = null;
      this.selectedArea = null;
      this.activePlanCells = [];
      this.workerItems = DEFAULT_WORKER('');
    },
    // Flash
    checkFlashingButtons() {
      if (this.activeMode) {
        this.flashState.assignBtn = false;
        this.flashState.removeBtn = false;
        this.flashState.planBtn = false;
        return;
      }

      let hasEmpty = false;
      let hasAssigned = false;
      Object.keys(this.highlightedCells).forEach(key => {
        if (this.selectedCells.has(key)) hasAssigned = true;
        else hasEmpty = true;
      });

      if (hasEmpty) this.triggerFlash('assignBtn');
      if (hasAssigned) {
        this.triggerFlash('removeBtn');
        this.triggerFlash('planBtn');
      }
    },
    triggerFlash(elementName) {
      this.flashState[elementName] = true;
      setTimeout(() => { this.flashState[elementName] = false; }, 1200);
    },
    // Calculations
    calcMinutesDiff(start, end) {
      if (!start || !end) return 0;
      const [sH, sM] = start.split(':').map(Number);
      const [eH, eM] = end.split(':').map(Number);
      let diffMinutes = (eH * 60 + eM) - (sH * 60 + sM);
      return diffMinutes < 0 ? diffMinutes + 1440 : diffMinutes;
    },
    calculateTaskTotals(workersArray) {
      const task = { ...this.baseTask, task_workers: workersArray || [] };

      task.smz_cost = Number(task.smz_cost) || 0;
      task.payment_first = Number(task.payment_first) || 0;
      task.executor_rate = Number(task.executor_rate) || 0;
      task.project_rate = Number(task.project_rate) || 0;

      const rateType = (task.location && task.location.rate_type) || task.rate_type || 1;

      task.total_worker_price = this.calculateWorkersPrice(task.task_workers);
      const smzFactor = task.smz_cost > 0 ? (task.smz_cost / 100) : 0;
      task.total_worker_price_with_comiss = task.total_worker_price * (1 + smzFactor);

      task.total_hours = this.calculateWorkersHours(task.task_workers);
      task.total_price = this.calculateTotalPrice(task);
      task.total_delta = this.calculateTotalDelta(task);

      task.total_manager = rateType === 2
        ? this.calculateTotalDeltaWithRateUnits(task.total_hours, task.executor_rate)
        : this.calculateTotalDeltaWithRatePercent(task.total_delta, task.executor_rate);

      if (task.project_id) {
        const deltaForProject = task.total_delta - task.total_manager;
        task.total_project = rateType === 2
          ? Math.round(task.total_hours * task.project_rate * 100) / 100
          : Math.round(deltaForProject * (task.project_rate / 100) * 100) / 100;
      } else {
        task.total_project = 0;
      }

      return task;
    },
    toggleTotalCalc() {
      if (this.calcMode === 'total') {
        this.showCalculations = !this.showCalculations;
      } else {
        this.calcMode = 'total';
        this.activeCalcDate = null;
        this.showCalculations = true;
      }
    },
    toggleIndividualCalc() {
      if (Object.keys(this.groupedPlannedWorkers).length === 0) {
        this.$bvToast.toast('Сначала назначьте и запланируйте исполнителей.', {
          title: 'Нет данных',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (this.calcMode === 'individual') {
        this.showCalculations = !this.showCalculations;
      } else {
        this.calcMode = 'individual';
        this.activeCalcDate = null;
        this.showCalculations = true;
      }
    },
    resetCalcToTotal() {
      if (this.calcMode !== 'total') {
        this.calcMode = 'total';
        this.activeCalcDate = null;
      }
    },
    handleHeaderClick(dateString) {
      if (this.calcMode === 'individual') {
        if (!this.groupedPlannedWorkers[dateString]) return;

        this.activeCalcDate = dateString;
        this.showCalculations = true;
        return;
      }

      this.toggleColumn(dateString);
    },
    // Popover
    openPopover(workerId, dateString, targetId) {
      if (!targetId) return;

      this.activePopover.targetId = targetId;
      this.activePopover.workerId = parseInt(workerId);
      this.activePopover.date = dateString;

      this.$nextTick(() => {
        const el = document.getElementById(targetId);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.bottom > window.innerHeight - 350) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        this.activePopover.show = true;
      });
    },
    editFromPopover() {
      this.activePopover.show = false;

      const workerId = this.activePopover.workerId;
      const date = this.activePopover.date;
      const key = this.getCellKey(workerId, date);

      this.activeMode = 'plan';
      this.$set(this.highlightedCells, key, true);
      this.activePlanCells = [{ workerId, date }];
      this.initSidebar();
    },
    removeFromPopover() {
      this.resetCalcToTotal();
      const key = this.getCellKey(this.activePopover.workerId, this.activePopover.date);
      this.selectedCells.delete(key);
      this.$delete(this.taskWorkerCells, key);
      this.selectedCells = new Set(this.selectedCells);
      this.activePopover.show = false;
      this.$emit('change', Array.from(this.selectedCells));
      this.$emit('workers-planned', Object.keys(this.taskWorkerCells).length);
    },
    applyWorkersPrice() {
      if (this.allWorkersPrice < 0) return;

      this.workers.forEach(worker => {
        this.$set(this.workerPrices, worker.id, this.allWorkersPrice);
      });

      if (this.$refs.price_popover) {
        this.$refs.price_popover.$emit('close');
      }
    },
    addOutsideClickListener() {
      document.addEventListener('click', this.handleOutsideClick);
    },
    removeOutsideClickListener() {
      document.removeEventListener('click', this.handleOutsideClick);
    },
    handleOutsideClick(e) {
      const popoverEl = document.querySelector('.timesheet-popover');
      const targetEl = document.getElementById(this.activePopover.targetId);

      if (popoverEl && !popoverEl.contains(e.target) && targetEl && !targetEl.contains(e.target)) {
        this.activePopover.show = false;
      }
    },
    // Services
    addService() {
      const newService = {
        id: 0,
        location_work_service_id: null,
        location_work_service_name: '',
        quantity: 0,
        price: 0,
        total_price: 0,
        unit_code: '539',
      };

      this.workerItems.task_worker_services.push(newService);
    },
    onServiceSelected(serviceId, index) {
      const locationServices = this.services.find(s => s.value === serviceId);
      const item = this.workerItems.task_worker_services[index];

      if (locationServices) {
        const s = locationServices.data;
        item.location_work_service_id = s.id;
        item.location_work_service_name = s.work_service_name;
        item.price = Number(s.price) || 0;
        item.unit_code = s.unit_code;
        item.total_price = item.price * (Number(item.quantity) || 0);
      }
    },
    onServiceQuantityInput(index, value) {
      const item = this.workerItems.task_worker_services[index];
      item.quantity = value === '' ? '' : Number(value);
      item.total_price = (Number(item.price) || 0) * (Number(item.quantity) || 0);
    },
    removeService(index) {
      this.workerItems.task_worker_services.splice(index, 1);
    },
  }
}
</script>

<style>
.timesheet-layout {
  position: relative;
  overflow: hidden;
}

.timesheet-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #fff;
  user-select: none;
}

.timesheet-grid {
  min-height: 0;
}

.timesheet-fixed-panel {
  flex-shrink: 0;
  width: 284px;
  z-index: 11;
  background: #fff;
  display: flex;
  flex-direction: column;
  margin-right: 4px;
}
.timesheet-header-fixed {
  flex-shrink: 0;
  padding-top: 5px;
}

.timesheet-body-fixed {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.timesheet-body-fixed .timesheet-row:last-child {
  margin-bottom: 10px;
}

.timesheet-scrollable-panel {
  overflow: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.timesheet-header-scrollable {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
  padding-top: 5px;
  min-width: max-content;
}

.timesheet-body-scrollable {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: max-content;
  clip-path: inset(0);
}

.timesheet-body-scrollable .timesheet-row:last-child {
  margin-bottom: 10px;
}

.timesheet-container.scroll-locked {
  overflow: hidden !important;
}

.timesheet-popover {
  z-index: 5000 !important;
}

.timesheet-row {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  width: max-content;
}

.timesheet-row:last-child {
  margin-bottom: 10px;
}

.worker-col-spacer {
  width: 284px;
  flex: none;
  height: 7px;
  background: #FFF;
}

.day-counter-cell {
  width: 32px;
  height: 7px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  color: #0A0F17;
  pointer-events: none;
  flex: none;
}

.worker-col-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  width: 200px;
  height: 48px;
  font-size: 13px;
  font-weight: 600;
  background: #F4F5F5 !important;;
  flex: none;
  border-radius: 6px;
}

.worker-col-name {
  font-size: 13px;
  width: 200px;
  background: #FFF;
  border-radius: 6px;
  align-self: stretch;
  line-height: 30px;
  flex: none;
}

.price-col-header {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 48px;
  background: #F4F5F5 !important;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  flex: none;
}

.price-col-cell {
  width: 80px !important;
  height: 30px;
  background: #FFF !important;
  border-radius: 6px;
  padding: 0;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(10, 15, 23, 0.05);
}

.price-cell-input {
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  text-align: center;
  font-size: 12px;
  color: #333;
  font-weight: 500;
}

.timesheet-row:hover .price-col-cell:not(.is-readonly) {
  background-color: rgba(10, 15, 23, 0.06);
}

.day-header-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  gap: 2px;
  width: 32px;
  height: 48px;
  background: rgba(10, 15, 23, 0.04);
  border-radius: 6px;
  flex: none;
  font-size: 11px;
  font-weight: 600;
  color: #333;
  transition: background-color 0.2s;
}

.day-header-cell.is-clickable {
  cursor: pointer;
}

.day-header-cell.is-clickable:hover {
  background: rgba(10, 15, 23, 0.1);
}

.day-header-cell.is-weekend {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.day-header-cell.is-clickable.is-weekend:hover {
  background: rgba(220, 53, 69, 0.15);
}

.day-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 32px;
  height: 30px;
  background: rgba(10, 15, 23, 0.02);
  border-radius: 6px;
  flex: none;
  cursor: pointer;
  transition: background-color 0.1s;
}

.day-header-cell.is-today {
  background-color: #ECF7F1 !important;
  border: 1px solid rgba(26, 145, 103, 0.3);
}

.day-cell.is-readonly {
  cursor: default;
}

.day-cell.is-selected {
  background-color: #1A9167;
  border-color: #1A9167;
}

/* Ховеры и перекрестия */

.timesheet-row:hover .day-cell:not(.is-readonly):not(.is-selected),
.day-cell.is-col-hovered:not(.is-readonly):not(.is-selected) {
  background-color: rgba(10, 15, 23, 0.06);
}

.timesheet-row:hover .day-cell.is-col-hovered:not(.is-readonly):not(.is-selected) {
  background-color: rgba(10, 15, 23, 0.12);
}

.timesheet-row:hover .day-cell.is-selected:not(.is-readonly),
.day-cell.is-col-hovered.is-selected:not(.is-readonly) {
  background-color: #147251;
}

.timesheet-row:hover .day-cell.is-col-hovered.is-selected:not(.is-readonly) {
  background-color: #0f5c40;
}

/* Выделение */

.day-cell.is-highlighted::after,
.day-cell.is-dragged::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: inherit;
  pointer-events: none;
  z-index: 3;
}

.day-cell.is-selected.is-highlighted::after,
.day-cell.is-selected.is-dragged::after {
  background: rgba(0, 0, 0, 0.3);
}

/* Анимация планирования */

@keyframes flashActiveCells {
  0%, 100% { background-color: #1A9167; box-shadow: none; }
  50% { background-color: #27d99b; box-shadow: 0 0 8px #27d99b; transform: scale(1.05); z-index: 5; }
}

.timesheet-container.is-flashing .day-cell.is-selected {
  animation: flashActiveCells 0.3s ease-in-out 2;
}

@keyframes flashButton {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.03); opacity: 0.8; }
}

button.custom-btn-secondary.is-btn-flashing {
  background-color: #1A9167 !important;
  color: #FFF !important;
  border-color: #1A9167 !important;
  animation: flashButton 0.4s ease-in-out 2;
}

.timesheet-container.is-planning .day-cell.is-disabled {
  opacity: 0.4;
}

.timesheet-container.is-planning .day-cell.is-selected {
  border: 1px solid #1A9167;
}

/* Прочее */
.meta-info-overlay {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  background-color: #f8f9fa;
  padding: 2px 8px;
  border-radius: 4px;
  color: #6c757d;
  border: 1px solid #dee2e6;
  pointer-events: none;
  white-space: nowrap;
}

.active-mode {
  background-color: #1A9167 !important;
  color: #FFF !important;
  border-color: #1A9167 !important;
  box-shadow: inset 0 3px 5px rgba(0,0,0,0.12);
}

.planning-sidebar {
  width: 320px;
  max-width: 320px;
  overflow: hidden;
  box-sizing: border-box;
}

#planning-tabs > ul.nav-tabs,
#planning-tabs .nav.nav-tabs {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  border-bottom: 1px solid #dee2e6 !important;
  width: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
}

#planning-tabs .nav-item {
  flex: 1 1 50% !important;
  width: 50% !important;
  margin: 0 !important;
}

#planning-tabs .nav-link {
  padding: 10px 8px !important;
  border: none !important;
  border-bottom: 2px solid transparent !important;
  background: transparent !important;
  display: block !important;
  text-align: center !important;
  font-size: 13px !important;
  line-height: 1.2 !important;
  white-space: normal !important;
  color: #6c757d !important;
}

#planning-tabs .nav-link.active {
  border-bottom: 2px solid #1A9167 !important;
  color: #1A9167 !important;
  font-weight: 600 !important;
}

.calculations-panel {
  min-height: 100px;
  max-height: 150px;
  height: auto;
  overflow: hidden;
}

.layout-slide-right-enter-active,
.layout-slide-right-leave-active {
  transition: max-width 0.7s cubic-bezier(0.2, 0, 0, 1), opacity 0.4s ease, padding 0.9s ease;
  overflow: hidden;
  white-space: nowrap;
}
.layout-slide-right-enter,
.layout-slide-right-leave-to {
  max-width: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  border-left-width: 0 !important;
  opacity: 0;
}

.layout-slide-up-enter-active,
.layout-slide-up-leave-active {
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
  min-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
  opacity 0.2s ease;
  overflow: hidden;
}
.layout-slide-up-enter,
.layout-slide-up-leave-to {
  max-height: 0 !important;
  min-height: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  border-top-width: 0 !important;
  opacity: 0;
}

.layout-slide-up-enter-to,
.layout-slide-up-leave {
  max-height: 150px !important;
  min-height: 100px !important;
}

.hardware-accel {
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: transform, scroll-position;
}

@keyframes hint-pulse-empty {
  0% { background-color: transparent; }
  50% { background-color: rgba(26, 145, 103, 0.08); }
  100% { background-color: transparent; }
}

@keyframes hint-pulse-selected {
  0% {
    filter: brightness(1);
    box-shadow: none;
  }
  50% {
    filter: brightness(1.2);
    box-shadow: inset 0 0 8px rgba(255, 255, 255, 0.3);
  }
  100% {
    filter: brightness(1);
    box-shadow: none;
  }
}

.flash-targets-assign .day-cell:not(.is-selected) {
  animation: hint-pulse-empty 0.8s ease-in-out;
}

.flash-targets-remove .day-cell.is-selected,
.flash-targets-plan .day-cell.is-selected {
  animation: hint-pulse-selected 0.8s ease-in-out;
}

@keyframes flash-header-bg {
  0%   { background-color: transparent; }
  50%  { background-color: #ECF7F1; }
  100% { background-color: transparent; }
}

.flash-calc-target {
  animation: flash-header-bg 1.2s infinite ease-in-out !important;
  cursor: pointer !important;
  border-bottom: 2px solid #1A9167 !important;
}

.flash-calc-target:hover {
  animation: none !important;
  background-color: #ECF7F1 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Tasks/TaskTimesheet.vue